// /emails/new — 3-step wizard to generate + edit a branded email.
//
// Steps:
//   1. campaign — pick 1 of 6 campaign types (cards)
//   2. template — pick 1 of 4 templates (cards). For campaign types that
//      need a product (launch / promo / abandoned-cart), also pick a
//      product on this step.
//   3. edit — preview rendered HTML in iframe, edit slots in side panel,
//      Download HTML / Copy HTML / Save & return.
//
// Resume editing an existing email via ?id=<email-id> — skips straight
// to step 3, loads the saved record.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppLayoutV2 from '../../components/feature/AppLayoutV2';
import { useBrand } from '../../hooks/useBrand';
import {
  generateEmail,
  getEmail,
  patchEmail,
  listProducts,
  EMAIL_CAMPAIGN_META,
  EMAIL_TEMPLATE_META,
  type EmailRecord,
  type EmailSlots,
  type EmailCampaignType,
  type EmailTemplateId,
  type UserProduct,
} from '../../lib/api';

type Step = 'campaign' | 'template' | 'edit';

const EMAIL_CREDIT_COST = 3;

export default function NewEmailWizardPage() {
  const navigate = useNavigate();
  const { activeBrand } = useBrand();
  const [searchParams] = useSearchParams();
  const resumeId = searchParams.get('id');

  const [step, setStep] = useState<Step>(resumeId ? 'edit' : 'campaign');
  const [campaign, setCampaign] = useState<EmailCampaignType | null>(null);
  const [templateId, setTemplateId] = useState<EmailTemplateId | null>(null);
  const [products, setProducts] = useState<UserProduct[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [customInput, setCustomInput] = useState('');

  const [email, setEmail] = useState<EmailRecord | null>(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing email when resuming
  const resumeStartedRef = useRef(false);
  useEffect(() => {
    if (!resumeId || resumeStartedRef.current) return;
    resumeStartedRef.current = true;
    getEmail(resumeId)
      .then((rec) => {
        setEmail(rec);
        setCampaign(rec.campaignType);
        setTemplateId(rec.templateId);
        setSelectedProductIds(rec.productIds);
        setStep('edit');
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not load email.'));
  }, [resumeId]);

  // Fetch products on entry to template step if campaign needs them
  useEffect(() => {
    if (step === 'template' && products.length === 0) {
      void listProducts().then(setProducts).catch(() => undefined);
    }
  }, [step, products.length]);

  const campaignMeta = EMAIL_CAMPAIGN_META.find((c) => c.id === campaign);
  const needsProduct = campaignMeta?.needsProduct ?? false;

  const handleGenerate = async () => {
    if (!campaign || !templateId) return;
    setGenerating(true);
    setError(null);
    try {
      const rec = await generateEmail({
        campaignType: campaign,
        templateId,
        productIds: selectedProductIds,
        customInput: customInput.trim() || null,
      });
      setEmail(rec);
      setStep('edit');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate email.');
    } finally {
      setGenerating(false);
    }
  };

  const handleSlotChange = async (patch: EmailSlots) => {
    if (!email) return;
    setSaving(true);
    try {
      const updated = await patchEmail(email.id, { slots: patch });
      setEmail(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateChange = async (newTemplateId: EmailTemplateId) => {
    if (!email) return;
    setSaving(true);
    try {
      const updated = await patchEmail(email.id, { templateId: newTemplateId });
      setEmail(updated);
      setTemplateId(newTemplateId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change template.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppLayoutV2>
      <div className="mx-auto px-6 py-8 max-w-[1500px]">

        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate('/emails')}
            className="w-10 h-10 rounded-xl border border-surface-dark/40 hover:border-primary/40 hover:text-primary flex items-center justify-center text-dark-muted transition-colors"
            title="Back to emails"
          >
            <i className="ri-arrow-left-line" />
          </button>
          <div className="flex-1">
            <h1 className="font-display font-bold text-dark text-xl">
              {step === 'edit' ? 'Edit email' : 'New email'}
            </h1>
            <p className="text-xs text-dark-muted">
              {step === 'campaign' && 'Step 1 of 3 · Pick a campaign type'}
              {step === 'template' && 'Step 2 of 3 · Pick a template'}
              {step === 'edit' && 'Step 3 of 3 · Edit + export'}
            </p>
          </div>
          {step !== 'edit' && (
            <div className="text-xs text-dark-muted hidden md:block">
              Active brand: <span className="font-semibold text-dark">{activeBrand?.name || 'None'}</span>
            </div>
          )}
        </div>

        {/* Error banner */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm border border-red-200 flex items-start justify-between gap-3">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="text-red-700/60 hover:text-red-700">
              <i className="ri-close-line" />
            </button>
          </div>
        )}

        {/* Step body */}
        {step === 'campaign' && (
          <CampaignStep
            value={campaign}
            onPick={(c) => { setCampaign(c); setStep('template'); }}
          />
        )}

        {step === 'template' && (
          <TemplateStep
            campaign={campaign}
            value={templateId}
            onPick={setTemplateId}
            needsProduct={needsProduct}
            products={products}
            selectedProductIds={selectedProductIds}
            onToggleProduct={(id) => setSelectedProductIds((prev) =>
              prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
            )}
            customInput={customInput}
            onCustomInputChange={setCustomInput}
            generating={generating}
            onBack={() => setStep('campaign')}
            onGenerate={handleGenerate}
          />
        )}

        {step === 'edit' && email && (
          <EditStep
            email={email}
            saving={saving}
            onSlotChange={handleSlotChange}
            onTemplateChange={handleTemplateChange}
          />
        )}

        {step === 'edit' && !email && (
          <div className="flex items-center justify-center py-20 text-dark-muted">
            <i className="ri-loader-4-line animate-spin text-2xl mr-2" />
            Loading…
          </div>
        )}

      </div>
    </AppLayoutV2>
  );
}

// ============================================================
// Step 1: Campaign picker
// ============================================================

function CampaignStep({
  value, onPick,
}: {
  value: EmailCampaignType | null;
  onPick: (c: EmailCampaignType) => void;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {EMAIL_CAMPAIGN_META.map((c) => {
        const active = value === c.id;
        return (
          <button
            key={c.id}
            onClick={() => onPick(c.id)}
            className={`rounded-2xl border-2 p-5 text-left transition-all ${
              active
                ? 'border-primary bg-primary-light/30 shadow-md shadow-primary/15'
                : 'border-surface-dark/30 bg-white hover:border-primary/40 hover:shadow-md'
            }`}
          >
            <div className={`w-10 h-10 rounded-xl ${active ? 'bg-primary text-white' : 'bg-primary/10 text-primary'} flex items-center justify-center mb-3 transition-colors`}>
              <i className={`${c.icon} text-xl`} />
            </div>
            <h3 className="font-display font-bold text-dark text-base mb-1">
              {c.label}
            </h3>
            <p className="text-xs text-dark-muted">
              {c.description}
            </p>
            {c.needsProduct && (
              <span className="inline-block mt-2 px-2 py-0.5 rounded-full bg-surface text-[10px] uppercase tracking-wider font-bold text-dark-muted">
                Uses a product
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// ============================================================
// Step 2: Template + optional product picker + custom input
// ============================================================

function TemplateStep({
  campaign, value, onPick, needsProduct, products, selectedProductIds, onToggleProduct,
  customInput, onCustomInputChange, generating, onBack, onGenerate,
}: {
  campaign: EmailCampaignType | null;
  value: EmailTemplateId | null;
  onPick: (t: EmailTemplateId) => void;
  needsProduct: boolean;
  products: UserProduct[];
  selectedProductIds: string[];
  onToggleProduct: (id: string) => void;
  customInput: string;
  onCustomInputChange: (v: string) => void;
  generating: boolean;
  onBack: () => void;
  onGenerate: () => void;
}) {
  const canGenerate = !!value && (!needsProduct || selectedProductIds.length > 0);
  return (
    <div className="space-y-6">
      {/* Templates */}
      <div>
        <h3 className="text-xs uppercase tracking-wider font-bold text-dark-muted mb-2">
          Template
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {EMAIL_TEMPLATE_META.map((t) => {
            const active = value === t.id;
            return (
              <button
                key={t.id}
                onClick={() => onPick(t.id)}
                className={`rounded-2xl border-2 p-4 text-left transition-all ${
                  active
                    ? 'border-primary bg-primary-light/30 shadow-md shadow-primary/15'
                    : 'border-surface-dark/30 bg-white hover:border-primary/40 hover:shadow-md'
                }`}
              >
                <div className="aspect-[5/3] rounded-lg bg-surface mb-3 flex items-center justify-center text-dark-muted/60">
                  <i className="ri-mail-line text-3xl" />
                </div>
                <h4 className="font-display font-bold text-dark text-sm mb-0.5">
                  {t.name} {!t.ready && <span className="text-[10px] text-amber-600 font-semibold">BETA</span>}
                </h4>
                <p className="text-[11px] text-dark-muted">
                  {t.description}
                </p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Products (only if campaign needs them) */}
      {needsProduct && (
        <div>
          <h3 className="text-xs uppercase tracking-wider font-bold text-dark-muted mb-2">
            Featured products
            <span className="ml-2 text-[10px] text-dark-muted/60">
              {selectedProductIds.length} selected
            </span>
          </h3>
          {products.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-surface-dark/40 bg-white p-8 text-center text-sm text-dark-muted">
              No products in your library yet. Add products on the My Products page first.
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2">
              {products.slice(0, 32).map((p) => {
                const on = selectedProductIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    onClick={() => onToggleProduct(p.id)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                      on ? 'border-primary' : 'border-surface-dark/30 opacity-60 hover:opacity-100'
                    }`}
                    title={p.name || ''}
                  >
                    {p.imageUrl ? (
                      // eslint-disable-next-line jsx-a11y/alt-text
                      <img src={p.imageUrl} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-surface" />
                    )}
                    {on && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center">
                        <i className="ri-check-line text-xs" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Custom input (optional free-text hook) */}
      <div>
        <h3 className="text-xs uppercase tracking-wider font-bold text-dark-muted mb-2">
          Additional context <span className="text-[10px] text-dark-muted/60 font-normal">(optional)</span>
        </h3>
        <input
          type="text"
          value={customInput}
          onChange={(e) => onCustomInputChange(e.target.value)}
          placeholder='e.g. "Black Friday 30% off, ends Sunday"'
          maxLength={200}
          className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
        />
        <p className="mt-1 text-[11px] text-dark-muted">
          Mention any specifics — discount terms, deadline, story angle, etc. We thread this into the prompt.
        </p>
      </div>

      {/* CTAs */}
      <div className="flex items-center justify-between pt-2">
        <button
          onClick={onBack}
          className="px-4 py-2 rounded-full border border-dark-muted/40 text-sm font-semibold text-dark hover:bg-surface transition-colors"
        >
          ← Back
        </button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-dark-muted">
            {EMAIL_CREDIT_COST} credit{EMAIL_CREDIT_COST === 1 ? '' : 's'} per generation
          </span>
          <button
            disabled={!canGenerate || generating}
            onClick={onGenerate}
            className="px-5 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generating ? (
              <>
                <i className="ri-loader-4-line animate-spin mr-1.5" />
                Generating…
              </>
            ) : (
              <>
                ✨ Generate ({EMAIL_CREDIT_COST} credits)
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Step 3: Preview + slot editor + export
// ============================================================

function EditStep({
  email, saving, onSlotChange, onTemplateChange,
}: {
  email: EmailRecord;
  saving: boolean;
  onSlotChange: (patch: EmailSlots) => Promise<void> | void;
  onTemplateChange: (t: EmailTemplateId) => Promise<void> | void;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-4">

      {/* Preview iframe */}
      <div className="rounded-2xl border-2 border-surface-dark/30 bg-white overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-surface-dark/20 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <i className="ri-eye-line text-dark-muted" />
            <span className="text-xs font-semibold text-dark-muted uppercase tracking-wider">Preview</span>
          </div>
          {saving && (
            <span className="text-[11px] text-primary inline-flex items-center gap-1">
              <i className="ri-loader-4-line animate-spin" />
              Saving…
            </span>
          )}
        </div>
        <iframe
          title="Email preview"
          srcDoc={email.html}
          sandbox=""
          className="w-full"
          style={{ height: '720px', border: 0, backgroundColor: '#f5f5f5' }}
        />
      </div>

      {/* Editor panel */}
      <div className="space-y-3">

        <ExportPanel email={email} />

        <SlotEditor
          email={email}
          onChange={onSlotChange}
        />

        <TemplateSwitcher
          current={email.templateId}
          onChange={onTemplateChange}
        />

      </div>
    </div>
  );
}

function ExportPanel({ email }: { email: EmailRecord }) {
  const [copied, setCopied] = useState(false);
  const handleDownload = () => {
    const blob = new Blob([email.html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const safeSubject = (email.slots.subject || 'email').replace(/[^a-z0-9-]+/gi, '-').toLowerCase();
    a.href = url;
    a.download = `${safeSubject || 'email'}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email.html);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — silent */
    }
  };
  return (
    <div className="rounded-2xl border-2 border-surface-dark/30 bg-white p-4 flex items-center gap-2">
      <button
        onClick={handleDownload}
        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-colors"
      >
        <i className="ri-download-line" />
        Download HTML
      </button>
      <button
        onClick={handleCopy}
        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full border border-primary/30 text-primary hover:bg-primary-light/50 text-sm font-bold transition-colors"
      >
        <i className={copied ? 'ri-check-line' : 'ri-clipboard-line'} />
        {copied ? 'Copied!' : 'Copy HTML'}
      </button>
    </div>
  );
}

function SlotEditor({
  email, onChange,
}: {
  email: EmailRecord;
  onChange: (patch: EmailSlots) => Promise<void> | void;
}) {
  // Local mirror of slots so typing feels instant. Server-save on blur.
  const [draft, setDraft] = useState<EmailSlots>(email.slots);

  useEffect(() => {
    // Sync from server when email refreshes (e.g. after a template switch)
    setDraft(email.slots);
  }, [email.slots]);

  const commit = (field: keyof EmailSlots) => () => {
    if ((draft[field] || '') !== (email.slots[field] || '')) {
      void onChange({ [field]: draft[field] ?? '' } as EmailSlots);
    }
  };

  return (
    <div className="rounded-2xl border-2 border-surface-dark/30 bg-white p-4">
      <h3 className="text-xs uppercase tracking-wider font-bold text-dark-muted mb-3">
        Content
      </h3>
      <div className="space-y-3">
        <Field label="Subject line" hint="Max 50 chars" maxLength={120}>
          <input
            type="text"
            value={draft.subject || ''}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            onBlur={commit('subject')}
            className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 focus:border-primary outline-none text-sm"
          />
        </Field>

        <Field label="Preview text" hint="Shows in inbox next to subject">
          <input
            type="text"
            value={draft.preheader || ''}
            onChange={(e) => setDraft({ ...draft, preheader: e.target.value })}
            onBlur={commit('preheader')}
            className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 focus:border-primary outline-none text-sm"
          />
        </Field>

        <Field label="Hero headline">
          <input
            type="text"
            value={draft.hero_headline || ''}
            onChange={(e) => setDraft({ ...draft, hero_headline: e.target.value })}
            onBlur={commit('hero_headline')}
            className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 focus:border-primary outline-none text-sm"
          />
        </Field>

        <Field label="Body">
          <textarea
            value={draft.body || ''}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            onBlur={commit('body')}
            rows={4}
            className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 focus:border-primary outline-none text-sm resize-none"
          />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label="CTA label">
            <input
              type="text"
              value={draft.cta_label || ''}
              onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })}
              onBlur={commit('cta_label')}
              className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 focus:border-primary outline-none text-sm"
            />
          </Field>
          <Field label="CTA URL">
            <input
              type="url"
              value={draft.cta_url || ''}
              onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })}
              onBlur={commit('cta_url')}
              className="w-full px-2.5 py-1.5 rounded-md border border-dark-muted/30 focus:border-primary outline-none text-sm"
            />
          </Field>
        </div>
      </div>
    </div>
  );
}

function TemplateSwitcher({
  current, onChange,
}: {
  current: EmailTemplateId;
  onChange: (t: EmailTemplateId) => Promise<void> | void;
}) {
  return (
    <div className="rounded-2xl border-2 border-surface-dark/30 bg-white p-4">
      <h3 className="text-xs uppercase tracking-wider font-bold text-dark-muted mb-3">
        Template
      </h3>
      <div className="grid grid-cols-2 gap-2">
        {EMAIL_TEMPLATE_META.map((t) => {
          const active = current === t.id;
          return (
            <button
              key={t.id}
              onClick={() => void onChange(t.id)}
              className={`rounded-lg border-2 p-3 text-left transition-all text-xs ${
                active
                  ? 'border-primary bg-primary-light/30'
                  : 'border-surface-dark/30 hover:border-primary/40'
              }`}
            >
              <div className="font-display font-bold text-dark mb-0.5">
                {t.name}
                {!t.ready && <span className="ml-1 text-[9px] text-amber-600">BETA</span>}
              </div>
              <div className="text-[10px] text-dark-muted">{t.description}</div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Field({
  label, hint, maxLength, children,
}: {
  label: string;
  hint?: string;
  maxLength?: number;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[11px] font-semibold text-dark">{label}</span>
        {(hint || maxLength) && (
          <span className="text-[10px] text-dark-muted/70">{hint}</span>
        )}
      </div>
      {children}
    </label>
  );
}
